// client/src/components/Finances/FinancesTab.js

import { useState } from "react";
import {
  Calendar,
  CreditCard,
  Calculator,
  TrendingUp,
  AlertTriangle,
  Clock,
} from "lucide-react";

import SummaryStrip from "./SummaryStrip.js";
import MortgageDetails from "./MortgageDetails.js";
import MonthlyCostBreakdown from "./MonthlyCostBreakdown.js";
import EquityGraph from "./EquityGraph.js";
import EquityStats from "./EquityStats.js";
import PropertyValueInsights from "./PropertyValueInsights.js";
import FinancialDocuments from "./FinancialDocuments.js";

import ActionsBar from "../Shared/ActionsBar.js";
import TabLayout from "../Shared/TabLayout.jsx";

// --------------------------------------------------
// NORMALIZATION LAYER
// --------------------------------------------------
function normalizeFinanceData(property) {
  if (!property) return {};

  const propertyValue =
    property.currentValue ??
    property.valuationEstimate ??
    property.appraisedValue ??
    property.purchasePrice ??
    0;

  const mortgage = property.mortgage ?? property.finances?.mortgage ?? {};

  const mortgageBalance =
    mortgage.currentBalance ??
    mortgage.balance ??
    0;

  const mortgageRate =
    mortgage.rate ??
    mortgage.interestRate ??
    null;

  const mortgagePayment =
    mortgage.payment ??
    mortgage.monthlyPayment ??
    0;

  const mortgageRenewal =
    mortgage.renewalDate ??
    mortgage.renewal ??
    "";

  const remainingAmortYears =
    mortgage.remainingAmortYears ??
    mortgage.remainingAmortization ??
    null;

  const liabilities =
    property.liabilities ??
    property.finances?.liabilities ??
    [];

  const monthly = property.monthly ?? property.finances?.monthly ?? {};

  const costs = {
    mortgagePayment,
    propertyTax: monthly.propertyTax ?? monthly.taxes ?? 0,
    insurance: monthly.insurance ?? 0,
    hydro: monthly.hydro ?? monthly.electricity ?? 0,
    gas: monthly.gas ?? 0,
    water: monthly.water ?? 0,
    internet: monthly.internet ?? 0,
    condoFees: monthly.condoFees ?? monthly.hoaFees ?? 0,
    maintenanceReserve:
      monthly.maintenanceReserve ??
      (propertyValue ? Math.round((propertyValue * 0.01) / 12) : 0),
  };

  const totalMonthlyCost = Object.values(costs).reduce(
    (sum, v) => sum + (Number(v) || 0),
    0
  );

  const equity = Math.max(propertyValue - mortgageBalance, 0);

  const valuation = {
    estimate: propertyValue,
    comps: property.valuation?.comps ?? [],
    trend: property.valuation?.trend ?? property.marketTrend ?? "Flat",
    confidence: property.valuation?.confidence ?? "Medium",
  };

  const documents =
    property.documents?.filter((doc) =>
      ["mortgage", "insurance", "tax", "statement", "appraisal"].some((tag) =>
        (doc.tags || []).includes(tag)
      )
    ) ?? property.documents ?? [];

  return {
    summary: {
      totalMonthlyCost,
      mortgage: {
        lender: mortgage.lender ?? mortgage.institution ?? "Unknown",
        balance: mortgageBalance,
        rate: mortgageRate,
        payment: mortgagePayment,
        renewalDate: mortgageRenewal,
        remainingAmortYears,
      },
      valuation,
      equity,
    },

    mortgage: {
      lender: mortgage.lender ?? mortgage.institution ?? "Unknown",
      balance: mortgageBalance,
      rate: mortgageRate,
      payment: mortgagePayment,
      renewalDate: mortgageRenewal,
      remainingAmortYears,
    },

    liabilities,
    costs: {
      ...costs,
      total: totalMonthlyCost,
    },

    valuation,
    documents,
  };
}

// --------------------------------------------------
// MAIN PAGE
// --------------------------------------------------
export default function FinancesTab({ property }) {
  const normalized = normalizeFinanceData(property);

  const [refiResult, setRefiResult] = useState(null);

  if (!property) {
    return (
      <div className="p-6 text-sm text-gray-500">
        No property selected.
      </div>
    );
  }

  // ------------------------- ACTION HANDLERS -------------------------

  const runRefinanceCheck = async () => {
    setRefiResult(null);

    try {
      const res = await fetch("/api/analytics/refinance", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mortgage: normalized.mortgage,
          new_rate: 0.039,
          new_amort_years: 25,
          penalty_amount: 5000,
          horizon_years: 5,
        }),
      });

      const data = await res.json();
      setRefiResult(data);
    } catch (err) {
      console.error("Refinance Check Error:", err);
    }
  };

  const runRenewalStrategy = () => {
    alert("Renewal strategy panel coming soon.");
  };

  const runCashFlowAnalysis = () => {
    alert("Cash flow optimizer coming soon.");
  };

  const goToAddProperty = () => {
    window.location.href = "/add-property";
  };

  // ----------------------------- HELPERS -----------------------------

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const target = new Date(dateStr);
    if (isNaN(target.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not set";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // ----------------------------- SIDEBAR -----------------------------

  const financeSidebar = (
    <div className="space-y-6">
      {/* Key Dates */}
      <div>
        <h3 className="font-semibold text-[#262626] mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#CC0202]" />
          Key Financial Dates
        </h3>
        <div className="space-y-3">
          {normalized.mortgage.renewalDate && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-amber-900">
                  Mortgage Renewal
                </span>
                {daysUntil(normalized.mortgage.renewalDate) !== null && (
                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                    {daysUntil(normalized.mortgage.renewalDate)} days
                  </span>
                )}
              </div>
              <p className="text-xs text-amber-700 mt-1">
                {formatDate(normalized.mortgage.renewalDate)}
              </p>
            </div>
          )}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <span className="text-sm font-medium text-gray-700">
              Property Tax Due
            </span>
            <p className="text-xs text-gray-500 mt-1">
              Next quarterly payment
            </p>
          </div>
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <span className="text-sm font-medium text-gray-700">
              Insurance Renewal
            </span>
            <p className="text-xs text-gray-500 mt-1">
              Annual review recommended
            </p>
          </div>
        </div>
      </div>

      {/* Monthly Summary */}
      <div>
        <h3 className="font-semibold text-[#262626] mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-[#CC0202]" />
          Monthly Overview
        </h3>
        <div className="p-4 bg-[#CC0202]/5 border border-[#CC0202]/20 rounded-lg">
          <div className="text-center">
            <p className="text-2xl font-bold text-[#CC0202]">
              ${normalized.costs.total?.toLocaleString() || "0"}
            </p>
            <p className="text-xs text-gray-600 mt-1">Total Monthly Cost</p>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <div className="text-center p-2 bg-white rounded">
              <p className="font-semibold text-gray-900">
                ${normalized.mortgage.payment?.toLocaleString() || "0"}
              </p>
              <p className="text-gray-500">Mortgage</p>
            </div>
            <div className="text-center p-2 bg-white rounded">
              <p className="font-semibold text-gray-900">
                ${(normalized.costs.total - normalized.mortgage.payment)?.toLocaleString() || "0"}
              </p>
              <p className="text-gray-500">Other</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Calculators */}
      <div>
        <h3 className="font-semibold text-[#262626] mb-3 flex items-center gap-2">
          <Calculator className="w-4 h-4 text-[#CC0202]" />
          Quick Tools
        </h3>
        <div className="space-y-2">
          <button
            onClick={runRefinanceCheck}
            className="w-full p-3 text-left bg-white border border-gray-200 rounded-lg hover:border-[#CC0202] hover:bg-red-50 transition"
          >
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#CC0202]" />
              <span className="text-sm font-medium">Refinance Check</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              See if refinancing makes sense
            </p>
          </button>
          <button
            onClick={runRenewalStrategy}
            className="w-full p-3 text-left bg-white border border-gray-200 rounded-lg hover:border-[#CC0202] hover:bg-red-50 transition"
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#CC0202]" />
              <span className="text-sm font-medium">Renewal Strategy</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Plan your mortgage renewal
            </p>
          </button>
          <button
            onClick={runCashFlowAnalysis}
            className="w-full p-3 text-left bg-white border border-gray-200 rounded-lg hover:border-[#CC0202] hover:bg-red-50 transition"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#CC0202]" />
              <span className="text-sm font-medium">Cash Flow Analysis</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Optimize your monthly budget
            </p>
          </button>
        </div>
      </div>

      {/* Equity Snapshot */}
      <div>
        <h3 className="font-semibold text-[#262626] mb-3">Equity Snapshot</h3>
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
          <p className="text-2xl font-bold text-emerald-700">
            ${normalized.summary.equity?.toLocaleString() || "0"}
          </p>
          <p className="text-xs text-emerald-600 mt-1">
            {normalized.summary.valuation?.estimate > 0
              ? `${((normalized.summary.equity / normalized.summary.valuation.estimate) * 100).toFixed(1)}% ownership`
              : "Home equity"}
          </p>
        </div>
      </div>
    </div>
  );

  // ----------------------------- RENDER -----------------------------

  return (
    <TabLayout
      sidebar={financeSidebar}
      sidebarTitle="Financial Summary"
      storageKey="finances-sidebar"
    >
      <div className="w-full flex flex-col gap-6">
        {/* ACTION BAR — LEFT ALIGNED, BRANDED */}
        <ActionsBar
          onRefi={runRefinanceCheck}
          onRenewal={runRenewalStrategy}
          onCashflow={runCashFlowAnalysis}
          onAdd={goToAddProperty}
        />

        {/* SUMMARY STRIP */}
        <SummaryStrip summary={normalized.summary} />

        {/* MORTGAGE DETAILS */}
        <MortgageDetails
          mortgage={normalized.mortgage}
          liabilities={normalized.liabilities}
        />

        {/* MONTHLY COST BREAKDOWN */}
        <MonthlyCostBreakdown costs={normalized.costs} />

        {/* EQUITY: GRAPH + STATS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <EquityGraph />
          <EquityStats equitySummary={normalized.summary} />
        </div>

        {/* PROPERTY VALUE INSIGHTS */}
        <PropertyValueInsights valuation={normalized.valuation} />

        {/* DOCUMENTS */}
        <FinancialDocuments documents={normalized.documents} />

        {/* OPTIONAL OUTPUT */}
        {refiResult && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
            <p className="font-semibold text-blue-800 mb-2">
              Refinance Analysis Result:
            </p>

            <pre className="text-xs text-blue-900">
              {JSON.stringify(refiResult, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </TabLayout>
  );
}
